import {
  Add as AddIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FilterAlt as FilterAltIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  CircularProgress
} from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';

import { useAuth } from '../../contexts/Auth';
import { orderService, OrderListRequest } from '../../services/orderService';

// Order types
interface OrderListItem {
  orderNumber: string;
  orderDate: string;
  customerCode: string;
  customerName: string;
  totalAmount: number;
  currency: string;
  status: string;
  orderType: string;
}

interface OrderFilterParams {
  orderNumber?: string;
  customerCode?: string;
  startDate?: string;
  endDate?: string;
  status?: string;
  orderType?: string;
}

const OrderListPage: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const location = useLocation();
  
  // Authentication kontrolü
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      enqueueSnackbar('Oturum açmanız gerekiyor', { variant: 'warning' });
      navigate('/login');
      return;
    }
  }, [isAuthenticated, authLoading, navigate, enqueueSnackbar]);
  
  // Get order type from URL path instead of URL params
  const orderType = location.pathname.includes('/purchase') ? 'wholesale-purchase' : 'wholesale';
  
  // Page title based on order type
  const getPageTitle = () => {
    if (location.pathname.includes('/purchase')) {
      return 'Satınalma Siparişleri';
    } else {
      return 'Satış Siparişleri';
    }
  };
  
  // State definitions
  const [orders, setOrders] = useState<OrderListItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');
  const [filterOpen, setFilterOpen] = useState<boolean>(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [selectedOrder, setSelectedOrder] = useState<string | null>(null);
  const [page, setPage] = useState<number>(1);
  const [pageSize] = useState<number>(20);
  const [totalCount, setTotalCount] = useState<number>(0);
  
  const [filters, setFilters] = useState<OrderFilterParams>({
    orderType: orderType
  });

  // Fetch orders data
  const fetchOrders = async () => {
    // Authentication kontrolü
    if (!isAuthenticated) {
      console.warn('Kullanıcı oturum açmamış, siparişler yüklenemiyor');
      return;
    }

    setLoading(true);
    try {
      const request: OrderListRequest = {
        ...filters,
        orderType: orderType,
        page: page,
        pageSize: pageSize
      };
      
      const response = await orderService.getOrders(request);
      setOrders(response.items || []);
      setTotalCount(response.totalCount || 0);
    } catch (error: any) {
      console.error('Error fetching orders:', error);
      
      // Token hatası özel olarak ele al
      if (error.message?.includes('Authentication token not found') || 
          error.message?.includes('Authentication token is invalid')) {
        enqueueSnackbar('Oturum süreniz dolmuş. Lütfen tekrar giriş yapın.', { variant: 'warning' });
        navigate('/login');
        return;
      }
      
      enqueueSnackbar('Siparişler yüklenirken hata oluştu', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount and filter changes
  useEffect(() => {
    // Sadece authentication tamamlandıktan sonra veri yükle
    if (!authLoading && isAuthenticated) {
      fetchOrders();
    }
  }, [orderType, filters, page, isAuthenticated, authLoading]);

  // Authentication yükleniyorsa loading göster
  if (authLoading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Authentication yoksa hiçbir şey render etme (useEffect zaten yönlendirme yapacak)
  if (!isAuthenticated) {
    return null;
  }

  // Handle search
  const handleSearch = () => {
    if (searchText.trim()) {
      setFilters({
        ...filters,
        orderNumber: searchText.trim()
      });
    } else {
      const { orderNumber, ...restFilters } = filters;
      setFilters(restFilters);
    }
    setPage(1); // Reset to first page when searching
  };

  // Handle filter changes
  const handleFilterChange = (field: keyof OrderFilterParams, value: any) => {
    setFilters({
      ...filters,
      [field]: value
    });
    setPage(1); // Reset to first page when filtering
  };

  // Clear filters
  const handleClearFilters = () => {
    setFilters({ orderType: orderType });
    setSearchText('');
    setPage(1);
  };

  // Navigation functions
  const handleViewDetails = (orderNumber: string) => {
    // URL yoluna göre yönlendirme yap
    if (location.pathname.includes('/purchase')) {
      navigate(`/orders/purchase/detail/${orderNumber}`);
    } else {
      navigate(`/orders/sales/detail/${orderNumber}`);
    }
  };

  const handleEditOrder = (orderNumber: string) => {
    // URL yoluna göre yönlendirme yap
    if (location.pathname.includes('/purchase')) {
      navigate(`/orders/purchase/edit/${orderNumber}`);
    } else {
      navigate(`/orders/sales/edit/${orderNumber}`);
    }
  };

  const handleCreateNew = () => {
    // URL yoluna göre yönlendirme yap
    if (location.pathname.includes('/purchase')) {
      navigate(`/orders/purchase/create`);
    } else {
      navigate(`/orders/sales/create`);
    }
  };

  // Delete order
  const handleDeleteOrder = async () => {
    if (!selectedOrder) return;
    
    try {
      await orderService.deleteOrder(selectedOrder);
      enqueueSnackbar('Sipariş başarıyla silindi', { variant: 'success' });
      fetchOrders();
    } catch (error) {
      console.error('Error deleting order:', error);
      enqueueSnackbar('Sipariş silinirken hata oluştu', { variant: 'error' });
    } finally {
      setDeleteDialogOpen(false);
      setSelectedOrder(null);
    }
  };

  // Format currency
  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: currency || 'TRY'
    }).format(amount);
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('tr-TR');
  };

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'pending':
      case 'bekliyor':
        return 'warning';
      case 'approved':
      case 'onaylandi':
        return 'success';
      case 'cancelled':
      case 'iptal':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
      <Paper elevation={1} sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" component="h1">
            {getPageTitle()}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateNew}
            sx={{ minWidth: 150 }}
          >
            Yeni Sipariş
          </Button>
        </Box>

        {/* Search and Filter Bar */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            <Box sx={{ flex: '1 1 300px', minWidth: 300 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Sipariş numarası ile ara..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                InputProps={{
                  endAdornment: (
                    <IconButton onClick={handleSearch} size="small">
                      <SearchIcon />
                    </IconButton>
                  )
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<FilterAltIcon />}
                onClick={() => setFilterOpen(true)}
                size="small"
              >
                Filtrele
              </Button>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchOrders}
                size="small"
              >
                Yenile
              </Button>
              <Button
                variant="text"
                onClick={handleClearFilters}
                size="small"
              >
                Temizle
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Loading State */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Orders Table */}
        {!loading && (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Sipariş No</TableCell>
                  <TableCell>Tarih</TableCell>
                  <TableCell>Müşteri Kodu</TableCell>
                  <TableCell>Müşteri Adı</TableCell>
                  <TableCell align="right">Tutar</TableCell>
                  <TableCell>Durum</TableCell>
                  <TableCell align="center">İşlemler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orders.map((order) => (
                  <TableRow key={order.orderNumber} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {order.orderNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>{formatDate(order.orderDate)}</TableCell>
                    <TableCell>{order.customerCode}</TableCell>
                    <TableCell>{order.customerName}</TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="medium">
                        {formatCurrency(order.totalAmount, order.currency)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={order.status}
                        color={getStatusColor(order.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                        <Tooltip title="Görüntüle">
                          <IconButton
                            size="small"
                            onClick={() => handleViewDetails(order.orderNumber)}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Düzenle">
                          <IconButton
                            size="small"
                            onClick={() => handleEditOrder(order.orderNumber)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Sil">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              setSelectedOrder(order.orderNumber);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                {orders.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        Sipariş bulunamadı
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Pagination Info */}
        {!loading && orders.length > 0 && (
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Toplam {totalCount} sipariş, sayfa {page} / {Math.ceil(totalCount / pageSize)}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
              >
                Önceki
              </Button>
              <Button
                size="small"
                disabled={page >= Math.ceil(totalCount / pageSize)}
                onClick={() => setPage(page + 1)}
              >
                Sonraki
              </Button>
            </Box>
          </Box>
        )}
      </Paper>

      {/* Filter Dialog */}
      <Dialog open={filterOpen} onClose={() => setFilterOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Sipariş Filtreleri</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mt: 1 }}>
            <Box>
              <TextField
                fullWidth
                label="Müşteri Kodu"
                value={filters.customerCode || ''}
                onChange={(e) => handleFilterChange('customerCode', e.target.value)}
              />
            </Box>
            <Box>
              <FormControl fullWidth>
                <InputLabel>Durum</InputLabel>
                <Select
                  value={filters.status || ''}
                  label="Durum"
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  <MenuItem value="">Tümü</MenuItem>
                  <MenuItem value="pending">Bekliyor</MenuItem>
                  <MenuItem value="approved">Onaylandı</MenuItem>
                  <MenuItem value="cancelled">İptal</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box>
              <TextField
                fullWidth
                type="date"
                label="Başlangıç Tarihi"
                value={filters.startDate || ''}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <Box>
              <TextField
                fullWidth
                type="date"
                label="Bitiş Tarihi"
                value={filters.endDate || ''}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFilterOpen(false)}>İptal</Button>
          <Button onClick={() => setFilterOpen(false)} variant="contained">
            Uygula
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Siparişi Sil</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Bu siparişi silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>İptal</Button>
          <Button onClick={handleDeleteOrder} color="error" variant="contained">
            Sil
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default OrderListPage;